/**
 * usePosMenus Hook
 *
 * React Query hooks for POS menus.
 * @see Sprint 25a - Gestion des Promotions
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { posMenusService, type PosMenuQueryParams, type PosMenuUpdateInput } from '../services/pos-menus.service';

/**
 * Query key factory for pos menus
 */
export const posMenuKeys = {
  all: ['pos-menus'] as const,
  lists: () => [...posMenuKeys.all, 'list'] as const,
  list: (params?: PosMenuQueryParams) => [...posMenuKeys.lists(), params] as const,
  details: () => [...posMenuKeys.all, 'detail'] as const,
  detail: (id: string) => [...posMenuKeys.details(), id] as const,
};

/**
 * Hook to fetch pos menus list
 */
export function usePosMenus(params?: PosMenuQueryParams) {
  return useQuery({
    queryKey: posMenuKeys.list(params),
    queryFn: () => posMenusService.list(params),
    placeholderData: (prev) => prev,
  });
}

/**
 * Hook to fetch single pos menu
 */
export function usePosMenu(id: string | null) {
  return useQuery({
    queryKey: posMenuKeys.detail(id!),
    queryFn: () => posMenusService.getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to update a pos menu (toggle isPromo)
 */
export function useUpdatePosMenu() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: PosMenuUpdateInput }) =>
      posMenusService.update(id, input),
    onSuccess: (_menu, variables) => {
      queryClient.invalidateQueries({ queryKey: posMenuKeys.all });
      toast.success(
        variables.input.isPromo
          ? 'Menu marque comme promotion'
          : 'Menu retire des promotions'
      );
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la modification');
    },
  });
}

/**
 * Hook to sync menus from Bill API
 */
export function useSyncMenus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => posMenusService.sync(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: posMenuKeys.all });
      toast.success(
        `Synchronisation terminee: ${data.created} crees, ${data.updated} mis a jour`
      );
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la synchronisation');
    },
  });
}
